<script setup lang="ts">
import { computed, provide, ref, watch } from "vue";

import { IWizardTabExpose, IWizardTabProps } from "@/components/ui/wizard/wizard-tab.types";
import { IWizardInject, IWizardOptBtnOpts, EWizardCloseState, WizardInject } from "@/components/ui/wizard/wizard.types";
import { TComponent } from "@/utils/component-utils";

import Dialog from "primevue/dialog";

import WizardTab from "@/components/ui/wizard/wizard-tab.vue";

interface IProps {
  title?: string;
  subtitle?: string;
  draggable?: boolean;
}

interface IEmits {
  (e: "show"): void;
  (e: "close", state: EWizardCloseState): void;
  (e: "finish"): void;
}

const props = defineProps<IProps>();
const emit = defineEmits<IEmits>();

const currentTabIndex = defineModel<number>("currentTabIndex", {
  required: false,
  default: 0,
});
const maxStep = ref(0);
const tabs = ref<Array<TComponent<typeof WizardTab, IWizardTabProps, IWizardTabExpose>>>([]);
const visible = defineModel<boolean>("visible", { default: false, required: false });
const isNextDisabled = ref(false);
const isPreviousDisabled = ref(false);
const optBtn = ref<IWizardOptBtnOpts | undefined>();
const isBackButtonHidden = ref(false);
const isFinished = ref<boolean>(false);
const wizardCloseState = ref<EWizardCloseState | undefined>(undefined);

provide<IWizardInject>(WizardInject, {
  addTab(item: TComponent<typeof WizardTab, IWizardTabProps, IWizardTabExpose>) {
    const index = tabCount.value;
    const tabId = `${item.props.title!.replace(/ /g, "")}${index}`;
    tabs.value.splice(index, 0, item);

    item.exposed.setId(tabId);

    if (index < currentTabIndex.value + 1) {
      maxStep.value = index;
      changeTab(currentTabIndex.value + 1, index);
    }
  },
  closeWizard(state: EWizardCloseState = EWizardCloseState.SUCCESS) {
    wizardCloseState.value = state;
    visible.value = false;
  },
  removeTab(item: TComponent<typeof WizardTab, IWizardTabProps, IWizardTabExpose>) {
    const index = tabs.value.indexOf(item);
    if (index === -1) {
      return;
    }

    maxStep.value = maxStep.value - 1;

    tabs.value.splice(index, 1);

    if (index === currentTabIndex.value) {
      changeTab(undefined, index);
    }
  },
  focusNextTab: focusNextTab,
  focusPrevTab: focusPrevTab,
  setNextDisabled: setNextDisabled,
  setPreviousDisabled: setPreviousDisabled,
  setOptionalButton: setOptionalButton,
  removeOptionalButton: removeOptionalButton,
  hideBackButton: hideBackButton,
  showBackButton: showBackButton,
  finishWizard: handleFinishWizard,
});

const tabCount = computed(() => {
  return tabs.value.length;
});

function hideBackButton(): void {
  isBackButtonHidden.value = true;
}

function showBackButton(): void {
  isBackButtonHidden.value = false;
}

function setNextDisabled(next: boolean) {
  isNextDisabled.value = next;
}

function setPreviousDisabled(next: boolean) {
  isPreviousDisabled.value = next;
}

function changeTab(previousIndex?: number, currentIndex?: number) {
  const oldTab = previousIndex !== undefined ? tabs.value[previousIndex] : undefined;
  const newTab = currentIndex !== undefined ? tabs.value[currentIndex] : undefined;

  if (oldTab) {
    oldTab.exposed.setActive(false);
  }
  if (newTab) {
    newTab.exposed.setActive(true);
  }

  if (currentIndex !== undefined && currentIndex > maxStep.value) {
    maxStep.value = currentIndex;
  }

  if (currentIndex !== undefined) {
    currentTabIndex.value = currentIndex;
  }
}

async function focusNextTab() {
  setNextDisabled(true);
  setPreviousDisabled(true);

  const currentTab = tabs.value[currentTabIndex.value];
  if (!(await currentTab.exposed.validate())) {
    setNextDisabled(false);
    setPreviousDisabled(false);

    return;
  }

  if (currentTabIndex.value + 1 >= tabs.value.length) {
    handleFinishWizard();
    return;
  }

  setNextDisabled(false);
  setPreviousDisabled(false);
  changeTab(currentTabIndex.value, currentTabIndex.value + 1);
}

function focusPrevTab() {
  if (currentTabIndex.value - 1 < 0) {
    return;
  }

  setNextDisabled(false);
  setPreviousDisabled(false);
  changeTab(currentTabIndex.value, currentTabIndex.value - 1);
}

function setOptionalButton(opts: IWizardOptBtnOpts): void {
  optBtn.value = opts;
}

function removeOptionalButton(): void {
  optBtn.value = undefined;
}

function handleFinishWizard() {
  isFinished.value = true;
  emit("finish");
  visible.value = false;
}

function resetWizardIndex() {
  currentTabIndex.value = 0;
}

function handleHideDialog() {
  resetWizardIndex();

  if (isFinished.value) {
    emit("close", EWizardCloseState.SUCCESS);
  } else {
    emit("close", wizardCloseState.value ?? EWizardCloseState.ABANDONED);
  }

  isFinished.value = false;
  wizardCloseState.value = undefined;
}

watch(visible, () => {
  if (visible.value) {
    emit("show");
    setNextDisabled(false);
    setPreviousDisabled(false);
  }
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    class="w-[26rem]"
    :draggable="props.draggable"
    modal
    @hide="handleHideDialog"
  >
    <template #header>
      <slot name="header">
        <div class="flex flex-col gap-2">
          <h3 class="flex w-full justify-left font-semibold">
            {{ props.title }}
          </h3>
          <span
            v-if="props.subtitle"
            class="flex w-full text-text-secondary justify-left !text-sm"
          >
            {{ props.subtitle }}
          </span>
        </div>
      </slot>
    </template>

    <div class="wizard">
      <slot
        :idx="currentTabIndex"
        :is-next-disabled="isNextDisabled"
        :is-previous-disabled="isPreviousDisabled"
        :tabs-length="tabs.length"
      />
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
.wizard {
  @apply w-full h-full;
}
</style>
